import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, KeyRound, Camera, Gauge, PackageCheck, Paperclip, ShieldCheck,
  Lock, Plus, X, AlertTriangle, Check, ExternalLink, RefreshCw,
} from "lucide-react";

const RETURN_METHODS = [
  ["handed_to_person", "Handed to a person"], ["delivered_to_office", "Delivered to an office"],
  ["drop_box", "Placed in a designated drop box"], ["mailed", "Mailed or shipped"],
  ["left_as_instructed", "Left as instructed"], ["other", "Other"],
];
const KEY_TYPES = [
  ["unit_key", "Unit key"], ["building_key", "Building key"], ["mailbox_key", "Mailbox key"],
  ["garage_remote", "Garage remote"], ["parking_pass", "Parking pass"], ["gate_fob", "Gate fob"],
  ["access_card", "Access card"], ["other", "Other"],
];
const METER_TYPES = [["electric", "Electricity"], ["gas", "Gas"], ["water", "Water"], ["other", "Other"]];
const UE = () => <span className="ml-1 text-[9px] font-bold uppercase tracking-wide text-amber-600 bg-amber-50 border border-amber-200 rounded px-1">user-entered</span>;

export default function KeyReturnCapsule() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [accessItems, setAccessItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showReview, setShowReview] = useState(false);

  const loadAccessItems = useCallback(async () => {
    try {
      const { data: r } = await api.get(`/properties/${propertyId}/access-items`);
      setAccessItems(r.items || []);
    } catch { /* non-blocking */ }
  }, [propertyId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cap, ph] = await Promise.all([
        api.get(`/properties/${propertyId}/key-return-capsule`),
        api.get(`/properties/${propertyId}/photos`).catch(() => ({ data: { photos: [] } })),
      ]);
      setData(cap.data);
      setPhotos((ph.data.photos || ph.data || []).filter((p) => !p.photo_purpose || p.photo_purpose !== "key_return_capsule" || true));
      await loadAccessItems();
    } catch (e) {
      if (e?.response?.status === 404) { toast.error("Property not found."); navigate("/dashboard"); return; }
      if (e?.response?.status === 409) { toast.error("Key Return Capsule is only for move-out properties."); navigate(`/property/${propertyId}`); return; }
    } finally { setLoading(false); }
  }, [propertyId, navigate, loadAccessItems]);

  useEffect(() => { load(); }, [load]);

  const draft = data?.draft;
  const sealed = data?.current_sealed;

  const startDraft = async () => {
    setBusy(true);
    try { await api.post(`/properties/${propertyId}/key-return-capsule`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start the capsule."); }
    finally { setBusy(false); }
  };

  const patch = async (fields) => {
    setBusy(true);
    try {
      const { data: res } = await api.patch(`/properties/${propertyId}/key-return-capsule`, fields);
      setData((d) => ({ ...d, draft: res.capsule, missing_requirements: res.missing_requirements, ready_to_seal: res.ready_to_seal }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save.");
    } finally { setBusy(false); }
  };

  const seal = async () => {
    setBusy(true);
    try {
      await api.post(`/properties/${propertyId}/key-return-capsule/seal`);
      toast.success("Capsule sealed."); setShowReview(false); await load();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det?.missing_requirements) toast.error("Not ready: " + det.missing_requirements[0]);
      else toast.error(det || "Couldn't seal.");
    } finally { setBusy(false); }
  };

  const correct = async () => {
    setBusy(true);
    try { await api.post(`/properties/${propertyId}/key-return-capsule/correct`); toast.info("Correction draft created."); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't create correction."); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen grid place-items-center bg-[#F8FAFC]" data-testid="krc-loading"><Loader2 className="animate-spin text-brand" size={28} /></div>;

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-28" data-testid="krc-page">
      <header className="sticky top-0 z-10 bg-white border-b border-[#E2E8F0] px-4 py-3 flex items-center gap-2">
        <button data-testid="krc-back" onClick={() => navigate(`/property/${propertyId}?tab=moveout`)} className="text-navy"><ArrowLeft size={20} /></button>
        <div className="min-w-0">
          <h1 className="font-heading font-bold text-navy text-base flex items-center gap-1.5"><KeyRound size={16} className="text-brand" /> Key Return Capsule</h1>
          <p className="text-[11px] text-[#64748B]">Final move-out & return record</p>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-4 space-y-4">
        {sealed && !draft ? (
          <SealedView cap={sealed} onCorrect={correct} busy={busy} />
        ) : !draft ? (
          <div className="rounded-2xl border border-[#E2E8F0] bg-white p-6 text-center" data-testid="krc-empty">
            <KeyRound className="mx-auto text-[#94A3B8]" size={28} />
            <p className="mt-2 font-semibold text-navy">Start your Key Return Capsule</p>
            <p className="text-xs text-[#64748B] mt-1">Record the final details of your move-out, including keys, final-condition photos and supporting return records. This is free.</p>
            <Button data-testid="krc-start" onClick={startDraft} disabled={busy} className="mt-3 rounded-xl bg-navy text-white">Start capsule</Button>
          </div>
        ) : (
          <DraftWorkflow
            draft={draft} photos={photos} patch={patch} busy={busy}
            missing={data.missing_requirements || []} ready={data.ready_to_seal}
            onReview={() => setShowReview(true)}
            propertyId={propertyId} accessItems={accessItems} reloadAccessItems={loadAccessItems}
          />
        )}
      </div>

      {showReview && draft && (
        <ReviewSheet draft={draft} photos={photos} missing={data.missing_requirements || []} ready={data.ready_to_seal}
          onClose={() => setShowReview(false)} onSeal={seal} busy={busy} />
      )}
    </div>
  );
}

function Section({ icon: Icon, title, optional, children }) {
  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-white p-4" >
      <h2 className="font-semibold text-navy text-sm flex items-center gap-1.5 mb-2">
        <Icon size={15} className="text-brand" /> {title}
        {optional && <span className="text-[10px] font-medium text-[#94A3B8]">(optional)</span>}
      </h2>
      {children}
    </div>
  );
}

function DraftWorkflow({ draft, photos, patch, busy, missing, ready, onReview, propertyId, accessItems, reloadAccessItems }) {
  const selected = draft.final_sweep_photo_ids || [];
  const toggle = (pid) => {
    const next = selected.includes(pid) ? selected.filter((x) => x !== pid) : [...selected, pid];
    patch({ final_sweep_photo_ids: next });
  };
  const keys = draft.keys || [];
  const meters = draft.meters || [];

  return (
    <>
      {/* readiness banner */}
      <div className={`rounded-xl p-3 text-sm ${ready ? "bg-emerald-50 border border-emerald-200 text-emerald-700" : "bg-amber-50 border border-amber-200 text-amber-700"}`} data-testid="krc-readiness">
        {ready ? <span className="flex items-center gap-1.5"><Check size={15} /> Ready to seal</span>
          : <div><p className="font-semibold flex items-center gap-1.5"><AlertTriangle size={15} /> Missing before sealing:</p>
            <ul className="mt-1 list-disc pl-5 text-[11px]">{missing.map((m, i) => <li key={i} data-testid={`krc-missing-${i}`}>{m}</li>)}</ul></div>}
      </div>

      <Section icon={Camera} title="Final sweep">
        <p className="text-[11px] text-[#64748B] mb-2">Select existing move-out photos of the unit's final condition. New surrender-specific photos captured from the capture screen are excluded from the Coverage Map.</p>
        <div className="grid grid-cols-3 gap-2" data-testid="krc-sweep-photos">
          {photos.length === 0 && <p className="text-xs text-[#94A3B8] col-span-3">No move-out photos yet — capture some first.</p>}
          {photos.map((p) => (
            <button key={p.photo_id} data-testid={`krc-photo-${p.photo_id}`} onClick={() => toggle(p.photo_id)}
              className={`relative aspect-square rounded-lg overflow-hidden border-2 ${selected.includes(p.photo_id) ? "border-brand" : "border-transparent"}`}>
              <img src={fileUrl(p.storage_path)} alt="" className="h-full w-full object-cover" />
              {selected.includes(p.photo_id) && <span className="absolute top-1 right-1 bg-brand text-white rounded-full p-0.5"><Check size={11} /></span>}
            </button>
          ))}
        </div>
        <textarea data-testid="krc-sweep-note" defaultValue={draft.final_sweep_note || ""} placeholder="Optional factual note"
          onBlur={(e) => patch({ final_sweep_note: e.target.value })} rows={2} className="mt-2 w-full text-xs border border-[#E2E8F0] rounded-lg p-2" />
      </Section>

      <Section icon={KeyRound} title="Keys & access devices">
        <label className="flex items-center gap-2 text-xs text-[#334155] mb-2">
          <input type="checkbox" data-testid="krc-no-keys" checked={!!draft.no_keys}
            onChange={(e) => patch({ no_keys: e.target.checked, keys: e.target.checked ? [] : keys })} />
          No physical keys or access devices to record
        </label>
        {!draft.no_keys && (
          <div className="space-y-2" data-testid="krc-keys">
            {keys.map((k, i) => {
              const linked = accessItems.find((a) => a.item_id === k.access_item_id);
              return (
              <div key={k.key_id || i} className="rounded-xl border border-[#E2E8F0] p-2.5 space-y-2" data-testid={`krc-key-${i}`}>
                <div className="flex items-center gap-1.5">
                  <select value={k.type} data-testid={`krc-key-type-${i}`} onChange={(e) => { const nk = [...keys]; nk[i] = { ...k, type: e.target.value }; patch({ keys: nk }); }}
                    className="text-xs border border-[#E2E8F0] rounded-lg p-1.5 flex-1">
                    {KEY_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  {k.access_item_id ? (
                    <span data-testid={`krc-key-count-fixed-${i}`} className="w-14 text-center text-xs text-[#64748B] border border-[#E2E8F0] rounded-lg p-1.5 bg-[#F8FAFC]">Qty 1</span>
                  ) : (
                    <input type="number" min="1" value={k.count || ""} data-testid={`krc-key-count-${i}`} placeholder="Qty"
                      onChange={(e) => { const nk = [...keys]; nk[i] = { ...k, count: Number(e.target.value) }; patch({ keys: nk }); }}
                      className="w-14 text-xs border border-[#E2E8F0] rounded-lg p-1.5" />
                  )}
                  <button data-testid={`krc-key-remove-${i}`} onClick={() => patch({ keys: keys.filter((_, j) => j !== i) })} className="text-[#94A3B8]"><X size={14} /></button>
                </div>
                <KeyItemLink
                  index={i} keyRow={k} linked={linked} accessItems={accessItems} busy={busy}
                  propertyId={propertyId} reloadAccessItems={reloadAccessItems}
                  onLink={(aid) => { const nk = [...keys]; nk[i] = { ...k, access_item_id: aid || null, count: aid ? 1 : (k.count || 1) }; patch({ keys: nk }); }}
                />
              </div>
              );
            })}
            <Button data-testid="krc-key-add" size="sm" variant="outline" disabled={busy}
              onClick={() => patch({ keys: [...keys, { type: "unit_key", count: 1 }] })} className="h-8 rounded-lg text-xs"><Plus size={13} className="mr-1" /> Add item</Button>
            <p className="text-[10px] text-[#94A3B8]">Counts are user-entered. Never enter full PINs, codes, passwords or full card numbers. Linking to an access item records one physical item per row.</p>
          </div>
        )}
      </Section>

      <Section icon={Gauge} title="Final meters" optional>
        <div className="space-y-2" data-testid="krc-meters">
          {meters.map((m, i) => (
            <div key={m.meter_id || i} className="flex items-center gap-1.5" data-testid={`krc-meter-${i}`}>
              <select value={m.type === "electricity" ? "electric" : (m.type || "electric")} onChange={(e) => { const nm = [...meters]; nm[i] = { ...m, type: e.target.value }; patch({ meters: nm }); }} className="text-xs border border-[#E2E8F0] rounded-lg p-1.5">
                {METER_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              <input value={m.value || ""} placeholder="Reading" data-testid={`krc-meter-value-${i}`} onChange={(e) => { const nm = [...meters]; nm[i] = { ...m, value: e.target.value }; patch({ meters: nm }); }} className="flex-1 text-xs border border-[#E2E8F0] rounded-lg p-1.5" />
              <input value={m.unit || ""} placeholder="Unit" onChange={(e) => { const nm = [...meters]; nm[i] = { ...m, unit: e.target.value }; patch({ meters: nm }); }} className="w-16 text-xs border border-[#E2E8F0] rounded-lg p-1.5" />
              <button onClick={() => patch({ meters: meters.filter((_, j) => j !== i) })} className="text-[#94A3B8]"><X size={14} /></button>
            </div>
          ))}
          <Button data-testid="krc-meter-add" size="sm" variant="outline" onClick={() => patch({ meters: [...meters, { meter_id: `krm_${(crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2)).replace(/-/g, "")}`, type: "electric", value: "", unit: "" }] })} className="h-8 rounded-lg text-xs"><Plus size={13} className="mr-1" /> Add reading</Button>
          <p className="text-[10px] text-[#94A3B8]">Readings are user-entered. TenantSeal does not read meters or calculate usage or charges.</p>
        </div>
      </Section>

      <Section icon={PackageCheck} title="Return details">
        <label className="text-[11px] font-semibold text-[#475569]">Return method<UE /></label>
        <select data-testid="krc-return-method" value={draft.return_method || ""} onChange={(e) => patch({ return_method: e.target.value })}
          className="mt-1 w-full text-sm border border-[#E2E8F0] rounded-lg p-2">
          <option value="">Select…</option>
          {RETURN_METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <label className="mt-2 block text-[11px] font-semibold text-[#475569]">Return date & time<UE /></label>
        <input type="datetime-local" data-testid="krc-return-datetime" defaultValue={(draft.return_datetime || "").slice(0, 16)}
          onBlur={(e) => patch({ return_datetime: e.target.value ? new Date(e.target.value).toISOString() : null })} className="mt-1 w-full text-sm border border-[#E2E8F0] rounded-lg p-2" />
        <input data-testid="krc-recipient" defaultValue={draft.recipient_name || ""} placeholder="Recipient / office (optional)" onBlur={(e) => patch({ recipient_name: e.target.value })} className="mt-2 w-full text-sm border border-[#E2E8F0] rounded-lg p-2" />
        <input data-testid="krc-tracking" defaultValue={draft.tracking_ref || ""} placeholder="Tracking / receipt reference (optional)" onBlur={(e) => patch({ tracking_ref: e.target.value })} className="mt-2 w-full text-sm border border-[#E2E8F0] rounded-lg p-2" />
        <textarea data-testid="krc-return-note" defaultValue={draft.return_note || ""} placeholder="Optional factual note" onBlur={(e) => patch({ return_note: e.target.value })} rows={2} className="mt-2 w-full text-xs border border-[#E2E8F0] rounded-lg p-2" />
      </Section>

      <Button data-testid="krc-review" onClick={onReview} disabled={busy} className="w-full h-12 rounded-xl bg-navy hover:bg-navy-hover text-white">
        Review & seal
      </Button>
    </>
  );
}

function ReviewSheet({ draft, photos, missing, ready, onClose, onSeal, busy }) {
  const [confirming, setConfirming] = useState(false);
  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-end sm:items-center justify-center" data-testid="krc-review-sheet" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold text-navy">Review & seal</h2>
          <button onClick={onClose} className="text-[#94A3B8]"><X size={18} /></button>
        </div>
        <div className="mt-3 space-y-2 text-sm text-[#334155]">
          <Row k="Return method" v={(RETURN_METHODS.find((m) => m[0] === draft.return_method) || [null, "—"])[1]} ue />
          <Row k="Return time" v={draft.return_datetime ? new Date(draft.return_datetime).toLocaleString() : "—"} ue />
          <Row k="Final-sweep photos" v={(draft.final_sweep_photo_ids || []).length} />
          <Row k="Keys / access devices" v={draft.no_keys ? "None recorded" : (draft.keys || []).length} ue />
          <Row k="Final meters" v={(draft.meters || []).length} ue />
        </div>
        {!ready && (
          <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3 text-amber-700 text-xs" data-testid="krc-review-missing">
            <p className="font-semibold flex items-center gap-1"><AlertTriangle size={13} /> Complete these first:</p>
            <ul className="mt-1 list-disc pl-4">{missing.map((m, i) => <li key={i}>{m}</li>)}</ul>
          </div>
        )}
        <p className="mt-3 text-[11px] text-[#64748B] bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-2">
          Sealing creates a tamper-evident manifest of the selected records. It does not verify that another party received the keys or accepted the return details.
        </p>
        <Button data-testid="krc-seal" disabled={!ready || busy} onClick={onSeal} className="mt-3 w-full h-12 rounded-xl bg-navy hover:bg-navy-hover text-white disabled:opacity-50">
          {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Lock size={16} className="mr-2" />} Seal Key Return Capsule
        </Button>
      </div>
    </div>
  );
}

function SealedView({ cap, onCorrect, busy }) {
  const { propertyId } = useParams();
  const verifyUrl = `${window.location.origin}/verify/${cap.manifest_fingerprint}`;
  const m = cap.manifest || {};
  const [inDossier, setInDossier] = useState(
    () => localStorage.getItem(`dd_capsule_dossier_${propertyId}`) === "1"
  );
  const toggleDossier = (next) => {
    setInDossier(next);
    localStorage.setItem(`dd_capsule_dossier_${propertyId}`, next ? "1" : "0");
    toast.success(next ? "This Capsule will be included in your Exhibit Dossier export."
                       : "This Capsule will not be included in the Exhibit Dossier export.");
  };
  return (
    <div className="space-y-4" data-testid="krc-sealed">
      <div className="rounded-2xl border border-emerald-200 bg-white p-5">
        <div className="flex items-center gap-2">
          <span className="grid place-items-center h-10 w-10 rounded-xl bg-emerald-50 text-emerald-700"><ShieldCheck size={20} /></span>
          <div><h2 className="font-heading text-lg font-bold text-navy">Capsule sealed</h2><p className="text-xs text-[#64748B]">Version {cap.version} · {cap.current ? "Current" : "Superseded"}</p></div>
        </div>
        <p className="mt-3 text-[11px] font-semibold text-[#475569]">Manifest fingerprint</p>
        <p className="text-[11px] font-mono break-all text-[#334155]" data-testid="krc-fingerprint">{cap.manifest_fingerprint}</p>
        <p className="mt-2 text-[11px] text-[#64748B]">Sealed at {cap.sealed_at ? new Date(cap.sealed_at).toLocaleString() : "—"}</p>
        <a href={verifyUrl} target="_blank" rel="noreferrer" data-testid="krc-verify-link" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:underline">
          <ExternalLink size={14} /> Open public verification
        </a>
        <p className="mt-3 text-[11px] text-[#64748B] bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-2">
          TenantSeal verifies that this Capsule's sealed manifest has not changed since the recorded sealing time. It does not verify receipt of keys, the truth of user-entered statements or any legal outcome.
        </p>
      </div>
      <div className="rounded-2xl border border-[#E2E8F0] bg-white p-4 text-sm text-[#334155] space-y-1.5">
        <Row k="Return method" v={(RETURN_METHODS.find((x) => x[0] === m.return_method) || [null, "—"])[1]} ue />
        <Row k="Return time" v={m.return_datetime_user_entered ? new Date(m.return_datetime_user_entered).toLocaleString() : "—"} ue />
        <Row k="Included photos" v={(m.photo_ids || []).length} />
        <Row k="Keys / access devices" v={m.no_physical_keys ? "None recorded" : (m.keys || []).length} ue />
        <Row k="Final meters" v={(m.meters || []).length} ue />
      </div>
      <div className="rounded-2xl border border-[#E2E8F0] bg-white p-4">
        <label className="flex items-start gap-2.5 cursor-pointer" data-testid="krc-dossier-toggle-label">
          <input
            type="checkbox"
            data-testid="krc-include-dossier"
            checked={inDossier}
            onChange={(e) => toggleDossier(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-[#CBD5E1] text-brand focus:ring-brand"
          />
          <span className="text-sm text-[#334155]">
            Include this sealed Capsule in the <span className="font-semibold text-navy">Exhibit Dossier</span> export
            <span className="block text-[11px] text-[#64748B] mt-0.5">Adds a Key Return Capsule section (fingerprint, verification QR and return details). Your photos are cross-referenced, never duplicated.</span>
          </span>
        </label>
      </div>
      <Button data-testid="krc-correct" variant="outline" onClick={onCorrect} disabled={busy} className="w-full h-11 rounded-xl">
        <RefreshCw size={14} className="mr-1.5" /> Create corrected version
      </Button>
    </div>
  );
}

const ACCESS_ITEM_TYPES = [
  ["door_key", "Door / mailbox key"], ["mailbox_key", "Mailbox key"], ["building_fob", "Building fob"],
  ["gate_remote", "Gate remote"], ["garage_remote", "Garage remote"], ["parking_permit", "Parking permit"],
  ["storage_access", "Storage access"], ["package_locker", "Package-locker access"],
  ["amenity_access", "Pool / gym / amenity access"], ["other", "Other access item"],
];

function KeyItemLink({ index, keyRow, linked, accessItems, busy, propertyId, reloadAccessItems, onLink }) {
  const [creating, setCreating] = useState(false);
  const onSelect = (val) => {
    if (val === "__create__") { setCreating(true); return; }
    onLink(val || null);
  };
  const unresolved = keyRow.access_item_id && !linked;
  return (
    <div className="flex items-center gap-1.5">
      <select
        data-testid={`krc-key-link-${index}`}
        value={keyRow.access_item_id || ""}
        disabled={busy}
        onChange={(e) => onSelect(e.target.value)}
        className="text-xs border border-[#E2E8F0] rounded-lg p-1.5 flex-1"
      >
        <option value="">Not linked to an access item</option>
        {accessItems.map((a) => (
          <option key={a.item_id} value={a.item_id}>
            {a.label}{a.partial_identifier ? ` · ${a.partial_identifier}` : ""}
          </option>
        ))}
        <option value="__create__">+ Create new access item…</option>
      </select>
      {linked && (
        <span data-testid={`krc-key-linked-${index}`} className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1 py-0.5 whitespace-nowrap">canonical</span>
      )}
      {unresolved && (
        <span data-testid={`krc-key-unresolved-${index}`} className="text-[10px] font-bold uppercase tracking-wide text-amber-600 bg-amber-50 border border-amber-200 rounded px-1 py-0.5 whitespace-nowrap">unresolved</span>
      )}
      {creating && (
        <CreateAccessItemModal
          propertyId={propertyId}
          onClose={() => setCreating(false)}
          onCreated={async (item) => { setCreating(false); await reloadAccessItems(); onLink(item.item_id); }}
        />
      )}
    </div>
  );
}

function CreateAccessItemModal({ propertyId, onClose, onCreated }) {
  const [type, setType] = useState("door_key");
  const [label, setLabel] = useState("");
  const [pid, setPid] = useState("");
  const [busy, setBusy] = useState(false);
  const actionId = useRef(`krc_ai_${(crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).slice(2)).replace(/-/g, "")}`);

  const save = async () => {
    if (!label.trim()) { toast.error("Give the item a label."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/properties/${propertyId}/access-items`, {
        item_type: type, label: label.trim(), partial_identifier: pid.trim() || null,
        client_action_id: actionId.current,
      });
      toast.success("Access item created and linked.");
      onCreated(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create that access item.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" data-testid="krc-create-item-modal" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-sm sm:rounded-2xl rounded-t-2xl p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-base font-bold text-navy">New access item</h3>
          <button onClick={onClose} className="text-[#94A3B8]"><X size={16} /></button>
        </div>
        <p className="text-[11px] text-[#64748B]">Use a short label — never a code, PIN, or full credential number. This creates one canonical item you can link to this key.</p>
        <div>
          <label className="text-[11px] font-semibold text-[#475569]">Type</label>
          <select data-testid="krc-create-item-type" value={type} onChange={(e) => setType(e.target.value)} className="mt-1 w-full text-sm border border-[#E2E8F0] rounded-lg p-2">
            {ACCESS_ITEM_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] font-semibold text-[#475569]">Label</label>
          <input data-testid="krc-create-item-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Main lobby fob" className="mt-1 w-full text-sm border border-[#E2E8F0] rounded-lg p-2" />
        </div>
        <div>
          <label className="text-[11px] font-semibold text-[#475569]">Short identifier (optional)</label>
          <input data-testid="krc-create-item-pid" value={pid} onChange={(e) => setPid(e.target.value)} placeholder="e.g. blue tag" className="mt-1 w-full text-sm border border-[#E2E8F0] rounded-lg p-2" />
        </div>
        <div className="flex gap-2 pt-1">
          <Button variant="outline" className="flex-1 rounded-xl" onClick={onClose}>Cancel</Button>
          <Button data-testid="krc-create-item-save" className="flex-1 rounded-xl bg-navy text-white" disabled={busy} onClick={save}>
            {busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Plus size={15} className="mr-1.5" />} Create & link
          </Button>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v, ue, icon: Icon, label, value, testId }) {
  const kk = k || label, vv = v ?? value;
  return (
    <div className="flex items-center justify-between gap-2" data-testid={testId}>
      <span className="text-[#64748B] text-xs flex items-center">{Icon && <Icon size={13} className="mr-1" />}{kk}{ue && <UE />}</span>
      <span className="text-navy font-medium text-sm text-right">{vv}</span>
    </div>
  );
}
